import React, { useEffect, useState } from "react";

import { ErrorIcon } from "../../../assets/icons/errorIcon";
import { useNearViewport } from "../../../hooks/useNearViewport";
import { LoaderWithText } from "../../../shared/Loader";

import MarkdownRenderer from "./MarkdownRenderer";

type TLargeParagraphRendererProps = {
    blobLink: string | null;
    description?: string;
    content?: string; // Add content prop for fallback
};

const LargeParagraphRenderer: React.FC<TLargeParagraphRendererProps> = ({
    blobLink,
    description,
    content: fallbackContent,
}) => {
    const [content, setContent] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { containerRef, isNearViewport } = useNearViewport();

    useEffect(() => {
        if (!isNearViewport) {
            return;
        }

        let isCancelled = false;
        const controller = new AbortController();

        // If we have fallback content, use it immediately
        if (fallbackContent) {
            setContent(fallbackContent);
            setError(null);
            setLoading(false);
            if (!blobLink)
                return;
        }

        // If no blobLink, show error
        if (!blobLink) {
            setError("No content link available");
            setLoading(false);
            return;
        }

        const fetchContent = async () => {
            try {
                if (!isCancelled) {
                    setError(null);
                    setLoading(true);
                }
                const response = await fetch(blobLink, {
                    signal: controller.signal,
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch content");
                }
                const text = await response.text();
                if (!isCancelled) {
                    setContent(text);
                    setLoading(false);
                }
            } catch (err: unknown) {
                if (isCancelled) {
                    return;
                }
                console.error("Error fetching large paragraph content:", err);
                setError("Failed to load content");
                setLoading(false);
            }
        };

        void fetchContent();

        return () => {
            isCancelled = true;
            controller.abort();
        };
    }, [blobLink, fallbackContent, isNearViewport]);

    if (!isNearViewport) {
        return (
            <div
                ref={containerRef}
                className="artifact-deferred-placeholder"
            >
                <span className="artifact-deferred-placeholder__eyebrow">
                    Long response
                </span>
                <p className="artifact-deferred-placeholder__title">
                    This response will load when you scroll here.
                </p>
                {description && (
                    <p className="artifact-deferred-placeholder__body">
                        {description}
                    </p>
                )}
            </div>
        );
    }

    if (loading) {
        return (
            <div className="blob-data-loader">
                <LoaderWithText text="Loading content..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="blob-data-error">
                <ErrorIcon />
                <span>{error}</span>
            </div>
        );
    }

    return <MarkdownRenderer content={content} />;
};

export default LargeParagraphRenderer;
